// Consent: what a person agreed to, which version of it, and when (spec/20 §6, spec/04 §2
// `consent`, spec/16 §3; F-CMP-06).
//
// Three kinds, and they are not alike:
//
//   terms, privacy   required to create an account, on every surface (the kiosk, the apps, player
//                    web). A new version of either is accepted as a **new row**; the old one stays.
//   marketing        optional, and **off unless the player switches it on**. Withdrawing it stamps
//                    `revokedAt` on the grant it withdraws, and a later opt-in is another new row.
//
// Nothing is overwritten: the rows are the record of truth and `player_profile.marketingOptIn`
// only mirrors the current marketing state.
//
// **Which version the client showed is part of the capture.** A client sends the version of each
// document it displayed; the server records exactly that. The documents themselves, their versions
// and their URLs, come from the brand configuration (spec/03 §12 "Legal", F-ORG-04) — the shape of
// what it will serve is `LegalDocuments` below. Where it is configured, a version that is not the
// one in force is refused with `consent-version-outdated`.
import { z } from "../zod.js";
import { OrganizationIdSchema, StationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "./scalars.js";
export const CONSENT_KINDS = ['terms', 'privacy', 'marketing'];
export const ConsentKindSchema = z
    .enum(CONSENT_KINDS)
    .openapi('ConsentKind', { description: 'What was agreed to: the terms, the privacy policy, or marketing.' });
/** The longest document version the server will record. A date or a short label, never prose. */
export const LEGAL_DOCUMENT_VERSION_MAX_LENGTH = 64;
/**
 * A document's version, as the brand configuration names it and the client showed it:
 * `2026-09-01`, `v3`, `2026.2`. Letters, digits, `.`, `_`, `:` and `-`, starting with a letter or a
 * digit — so it can be logged, compared and put in a CSV without escaping anything.
 */
export const LegalDocumentVersionSchema = z
    .string()
    .min(1)
    .max(LEGAL_DOCUMENT_VERSION_MAX_LENGTH)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/, 'Expected a document version such as "2026-09-01"')
    .openapi('LegalDocumentVersion', {
    description: 'The version of a legal document, as the brand configuration names it.',
    example: '2026-09-01',
});
/**
 * What a player agrees to when an account is created (spec/12 §3.3, spec/20 §6).
 *
 * Sent **with** the request that creates the account — `/v1/auth/sign-up/email`,
 * `/v1/auth/phone-number/verify`, `POST /v1/station/sign-in/phone` — so the account and its consent
 * rows are written in one transaction, or neither is. Its presence is the acceptance: `terms` and
 * `privacy` carry the version of each document the player was shown and ticked.
 *
 * `marketing` is `false` unless the player switched it on; leaving it out is `false`.
 */
export const SignUpConsentSchema = z
    .object({
    /** The version of the terms the player was shown and accepted. */
    terms: LegalDocumentVersionSchema,
    /** The version of the privacy policy the player was shown and accepted. */
    privacy: LegalDocumentVersionSchema,
    /** Marketing, off unless the player switched it on. Recorded under the privacy version above. */
    marketing: z.boolean().optional(),
})
    .strict()
    .openapi('SignUpConsent', {
    description: 'Terms and privacy (required) and marketing (optional, off by default), captured with the request ' +
        'that creates the account.',
});
/** Where in the product a consent was given. */
export const CONSENT_FLOWS = ['sign_up', 'profile', 'notification_prefs', 'document_acceptance'];
/**
 * How the account being created signed up, for a consent captured at sign-up: by phone code, by
 * email and password, by an email code at a kiosk (F-KIO-04), or through Google, Discord or Steam (F-IAM-03 … F-IAM-05, issue #418).
 */
export const CONSENT_SIGN_UP_METHODS = ['phone_otp', 'email_password', 'email_otp', 'google', 'discord', 'steam'];
/**
 * How and where a consent was given (spec/04 §2 `consent.source`). The surface and the place are
 * present only when the server knows them: a kiosk sign-up does, a request to `/v1/auth/*` does not.
 *
 * `method` is a string on the wire, not an enum, although the server only ever writes one of
 * `CONSENT_SIGN_UP_METHODS`: every new way to sign in adds a value (passkeys are next), and a client
 * that only shows it must not break when one does — the reason `AuthSession.secondFactor` is a string.
 */
export const ConsentSourceSchema = z
    .object({
    flow: z.enum(CONSENT_FLOWS),
    method: z
        .string()
        .optional()
        .openapi({
        description: `How the account was created, for a sign-up consent: one of ${CONSENT_SIGN_UP_METHODS.join(', ')}, and more as sign-in methods are added.`,
        example: 'phone_otp',
    }),
    surface: z.enum(['kiosk']).optional(),
    organizationId: OrganizationIdSchema.optional(),
    venueId: VenueIdSchema.optional(),
    stationId: StationIdSchema.optional(),
})
    .openapi('ConsentSource', { description: 'How and where a consent was given.' });
/** A document version the player has accepted, and when. */
export const AcceptedDocumentSchema = z
    .object({
    version: LegalDocumentVersionSchema,
    acceptedAt: IsoDateTimeSchema,
})
    .openapi('AcceptedDocument', { description: 'The latest version of a document the player accepted, and when.' });
/**
 * The player's marketing consent as it stands now. `grantedAt` and `version` belong to the grant in
 * force and are absent when there is none.
 */
export const MarketingConsentSchema = z
    .object({
    optIn: z.boolean(),
    grantedAt: IsoDateTimeSchema.optional(),
    /** The privacy policy version the grant was given under. */
    version: LegalDocumentVersionSchema.optional(),
})
    .openapi('MarketingConsent', { description: 'Whether the player has opted in to marketing, and since when.' });
/** Where a document lives and which version of it is in force. */
export const LegalDocumentSchema = z
    .object({
    version: LegalDocumentVersionSchema,
    url: z.url(),
})
    .openapi('LegalDocument', { description: 'A legal document: the version in force and where to read it.' });
/**
 * The legal documents the brand configuration names (spec/03 §12.4, F-ORG-04): what a client shows
 * before it asks for consent, and what the server checks a capture against. `GET /v1/meta` serves
 * them, with the Operator's copy, as `brand.legal` (`BrandLegalSchema`).
 */
export const RefundPolicyLinkSchema = z
    .object({ url: z.url() })
    .openapi('RefundPolicyLink', { description: 'Where the refund policy lives. It is shown, not consented to.' });
export const LegalDocumentsSchema = z
    .object({
    terms: LegalDocumentSchema,
    privacy: LegalDocumentSchema,
    refund: RefundPolicyLinkSchema,
})
    .openapi('LegalDocuments', { description: 'The terms, privacy policy and refund policy in force.' });
/**
 * The channels a notification can reach a person by (spec/16 §2). Marketing on each is governed by
 * the marketing consent and by the channel's own switch (spec/16 §3).
 */
export const NOTIFICATION_CHANNELS = ['inbox', 'push', 'whatsapp', 'sms', 'email'];
export const NotificationChannelSchema = z
    .enum(NOTIFICATION_CHANNELS)
    .openapi('NotificationChannel', { description: 'How a notification reaches a person.' });
/**
 * Whether marketing (`news`, offers) may use a channel when the player has never touched that
 * channel's switch (spec/16 §3): "marketing defaults **off** for push and requires opt-in for
 * WhatsApp, SMS and email". The inbox has no switch for it; the marketing consent alone governs it.
 * With no consent in force, no channel carries marketing at all.
 */
export const MARKETING_CHANNEL_DEFAULTS = {
    inbox: true,
    push: false,
    whatsapp: false,
    sms: false,
    email: false,
};
//# sourceMappingURL=consent.js.map